import { useContext, useMemo } from 'react';
import { AuthContext, type ImpersonationState, type UserProfile } from '../contexts/AuthContext';

/**
 * useTenantScopeId (HOF-101) — which tenant a BROWSER-LOCAL preference belongs to.
 *
 * A `localStorage` preference outlives a login, so it needs a namespace, and the
 * only correct namespace is the tenant whose data is on screen. The app has
 * impersonation („Anzeige als"), so „the logged-in user" is NOT that tenant:
 * `/auth/me` is proxied with the session's own `internal_access_token`
 * (`bff-service/src/api/auth.py::get_current_user`), so under impersonation the
 * profile still describes the ADMIN while every data read is served for the
 * customer. Keying off the profile alone would file a customer's preference
 * under the operator's name — and hand the operator's own back in a customer
 * context.
 *
 * ⚠️ This is a namespace for a PREFERENCE, not an authorisation claim. Nothing
 * here is sent anywhere, and it must never be used to decide what may be read:
 * that is the BFF session's `effective_user_id` and stays server-side.
 *
 * For the signed-in path the answer is not inferred here at all: `/auth/me`
 * already carries `effective_user_id`, the server's own
 * `get_effective_user_id` — an employee's inviter (the farm owner), a farmer's
 * own id. Re-deriving that rule client-side would be a second copy of it, free
 * to drift. (An earlier draft did exactly that, off a `UserProfile.id` the
 * response has never carried; codex round 1 caught it. See `AuthContext`.)
 *
 * Precision, stated rather than assumed: the impersonated branch cannot use the
 * same field — the frontend is given the target's USER id, not their tenant — so
 * an admin impersonating two employees of one farm gets two preference slots for
 * it. That is a FINER partition than the tenant, never a coarser one: it can
 * duplicate a preference, it cannot leak one across tenants.
 *
 * ⚠️ *Resolved* and *tenantless* are different answers, deliberately. Auth
 * settling means the question has been ASKED; it does not mean it was answered.
 * An unknown impersonation status yields `resolved: true` with `tenantId: null`
 * — the caller then persists nothing and behaves exactly as it did before it had
 * a preference, which is the same degradation it already owes a blocked
 * `localStorage`. Expressing that as *unresolved* instead would strand every
 * consumer waiting for an answer that is not coming.
 */

export interface TenantScope {
  /** Namespace for a per-tenant local preference, or null when there is none. */
  tenantId: string | null;
  /**
   * Whether the answer is final. `false` means auth is still in flight and
   * `tenantId` is withheld — a caller must WAIT rather than conclude "this
   * tenant has no preference", which is indistinguishable from it.
   */
  resolved: boolean;
}

/** The pure derivation, so it can be tested without a provider. */
export function tenantScopeId(
  profile: UserProfile | null,
  impersonation: ImpersonationState | null,
): string | null {
  // A `null` impersonation record is UNKNOWN, not "not impersonating" — and the
  // difference is the whole of AC 4. `AuthContext` maps a failed
  // `/admin/impersonation-status` to null, so reading the absence as a negative
  // would hand the operator's own tenant to a customer's view precisely when the
  // app has lost track of whose view it is. There is no safe guess, so there is
  // no guess: no statement ⇒ no tenant ⇒ nothing read and nothing written.
  if (!impersonation) return null;
  // Active but nameless is the same refusal: we know it is NOT the profile's
  // tenant, and we do not know whose it is.
  if (impersonation.active) return impersonation.user_id ?? null;
  if (!profile) return null;
  // `|| user_id` is a floor, not a second rule: a response missing the tenant
  // field must degrade to a narrower namespace, never to a shared one.
  return profile.effective_user_id || profile.user_id || null;
}

/**
 * Read the current tenant scope. Uses `useContext` rather than `useAuth`
 * deliberately (#2954d's exported context): a page that merely wants a
 * preference namespace must not throw — or force an `AuthProvider` on every
 * renderer — when there is no auth around it. Outside a provider there is
 * nothing to wait for, so the scope is *resolved and tenantless*: the caller
 * persists nothing and behaves exactly as it did before it had a preference.
 */
export function useTenantScopeId(): TenantScope {
  const auth = useContext(AuthContext);
  const profile = auth?.userProfile ?? null;
  const impersonation = auth?.impersonation ?? null;
  const resolved = auth ? !auth.isLoading : true;

  return useMemo(
    () => ({ tenantId: resolved ? tenantScopeId(profile, impersonation) : null, resolved }),
    [resolved, profile, impersonation],
  );
}
